'use client'

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { XIcon } from '@phosphor-icons/react'
import type React from 'react'

import { Button } from './button'
import { ScrollArea } from './scroll-area'
import { cn } from '../lib/utils'

export const DialogCreateHandle: typeof DialogPrimitive.createHandle = DialogPrimitive.createHandle

export const Dialog: typeof DialogPrimitive.Root = DialogPrimitive.Root

export const DialogPortal: typeof DialogPrimitive.Portal = DialogPrimitive.Portal

export function DialogTrigger(props: DialogPrimitive.Trigger.Props): React.ReactElement {
	return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

export function DialogClose(props: DialogPrimitive.Close.Props): React.ReactElement {
	return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

export function DialogBackdrop({ className, ...props }: DialogPrimitive.Backdrop.Props): React.ReactElement {
	return (
		<DialogPrimitive.Backdrop
			className={cn(
				'fixed inset-0 z-50 bg-black/32 backdrop-blur-sm transition-all duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0',
				className,
			)}
			data-slot="dialog-backdrop"
			{...props}
		/>
	)
}

export function DialogViewport({ className, ...props }: DialogPrimitive.Viewport.Props): React.ReactElement {
	return (
		<DialogPrimitive.Viewport
			className={cn('fixed inset-0 z-50 grid grid-rows-[1fr_auto_3fr] justify-items-center p-4', className)}
			data-slot="dialog-viewport"
			{...props}
		/>
	)
}

export function DialogPopup({
	className,
	children,
	showCloseButton = true,
	bottomStickOnMobile = true,
	closeProps,
	...props
}: DialogPrimitive.Popup.Props & {
	showCloseButton?: boolean
	bottomStickOnMobile?: boolean
	closeProps?: DialogPrimitive.Close.Props
}): React.ReactElement {
	return (
		<DialogPortal>
			<DialogBackdrop />
			<DialogViewport className={cn(bottomStickOnMobile && 'max-sm:grid-rows-[1fr_auto] max-sm:p-0 max-sm:pt-12')}>
				<DialogPrimitive.Popup
					className={cn(
						'bg-popover text-popover-foreground relative row-start-2 flex max-h-full min-h-0 w-full max-w-lg min-w-0 origin-center flex-col rounded-2xl border opacity-[calc(1-var(--nested-dialogs))] shadow-lg/5 transition-[scale,opacity,translate] duration-200 ease-in-out will-change-transform not-dark:bg-clip-padding before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-2xl)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] data-ending-style:opacity-0 data-starting-style:opacity-0 sm:scale-[calc(1-0.1*var(--nested-dialogs))] sm:data-ending-style:scale-98 sm:data-starting-style:scale-98 dark:before:shadow-[0_-1px_--theme(--color-white/6%)]',
						bottomStickOnMobile &&
							'max-sm:max-w-none max-sm:origin-bottom max-sm:rounded-none max-sm:border-x-0 max-sm:border-t max-sm:border-b-0 max-sm:before:hidden max-sm:before:rounded-none max-sm:data-ending-style:translate-y-4 max-sm:data-starting-style:translate-y-4',
						className,
					)}
					data-slot="dialog-popup"
					{...props}
				>
					{children}
					{showCloseButton && (
						<DialogPrimitive.Close
							aria-label="Close"
							className="absolute end-2 top-2"
							render={<Button size="icon" variant="ghost" />}
							{...closeProps}
						>
							<XIcon weight="thin" />
						</DialogPrimitive.Close>
					)}
				</DialogPrimitive.Popup>
			</DialogViewport>
		</DialogPortal>
	)
}

export function DialogHeader({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement {
	return (
		<div
			className={cn(
				'flex flex-col gap-2 p-6 in-[[data-slot=dialog-popup]:has([data-slot=dialog-panel])]:pb-3 max-sm:pb-4',
				className,
			)}
			data-slot="dialog-header"
			{...props}
		/>
	)
}

export function DialogFooter({
	className,
	variant = 'default',
	...props
}: React.ComponentProps<'div'> & {
	variant?: 'default' | 'bare'
}): React.ReactElement {
	return (
		<div
			className={cn(
				'flex flex-col-reverse gap-2 px-6 sm:flex-row sm:justify-end sm:rounded-b-[calc(var(--radius-2xl)-1px)]',
				variant === 'default' && 'bg-muted/72 border-t py-4',
				variant === 'bare' && 'pt-4 pb-6 in-[[data-slot=dialog-popup]:has([data-slot=dialog-panel])]:pt-3',
				className,
			)}
			data-slot="dialog-footer"
			{...props}
		/>
	)
}

export function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props): React.ReactElement {
	return (
		<DialogPrimitive.Title
			className={cn('font-heading text-xl leading-none font-semibold', className)}
			data-slot="dialog-title"
			{...props}
		/>
	)
}

export function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props): React.ReactElement {
	return (
		<DialogPrimitive.Description
			className={cn('text-muted-foreground text-sm', className)}
			data-slot="dialog-description"
			{...props}
		/>
	)
}

export function DialogPanel({
	className,
	scrollFade = true,
	...props
}: React.ComponentProps<'div'> & { scrollFade?: boolean }): React.ReactElement {
	return (
		<ScrollArea scrollFade={scrollFade}>
			<div
				className={cn(
					'p-6 in-[[data-slot=dialog-popup]:has([data-slot=dialog-footer]:not(.border-t))]:pb-1 in-[[data-slot=dialog-popup]:has([data-slot=dialog-header])]:pt-1',
					className,
				)}
				data-slot="dialog-panel"
				{...props}
			/>
		</ScrollArea>
	)
}

export { DialogPrimitive, DialogBackdrop as DialogOverlay, DialogPopup as DialogContent }
